export const PASSWORD_LENGTH = 6;

/** Deja solo los dígitos y recorta a la longitud de la clave */
export const onlyDigits = (value?: string | null): string => (value ?? '')
  .replace(/\D/g, '')
  .slice(0, PASSWORD_LENGTH);

const isConsecutive = (password: string, step: 1 | -1) => password
  .split('')
  .every((digit, index) => index === 0 || Number(digit) - Number(password[index - 1]) === step);

export const PASSWORD_MISMATCH_ERROR = 'Las contraseñas no coinciden';

export const PASSWORD_REUSED_ERROR = 'Contraseña no válida. Prueba con otra diferente';

/** Devuelve el motivo por el que una clave nueva no es válida, o null si lo es */
export const getPasswordStrengthError = (password: string): string | null => {
  if (/^(\d)\1+$/.test(password)) {
    return 'No puedes repetir el mismo dígito seis veces';
  }
  if (isConsecutive(password, 1) || isConsecutive(password, -1)) {
    return 'No puedes usar seis dígitos consecutivos';
  }
  return null;
};

/** Clave actual que no cumple los requisitos de seguridad (se obliga a cambiarla tras el login) */
export const isWeakPassword = (password: string): boolean => !/^\d{6}$/.test(password)
  || getPasswordStrengthError(password) !== null;

/** Login rechazado (403 USR0032) porque hay que actualizar la clave antes de entrar */
export const isPasswordExpiredError = (error: any): boolean => !!error?.result?.errors
  ?.some((e: { code: string }) => e.code === 'USR0032');

/* Validación conjunta de "nueva clave" + "repite la clave".
  `previousPassword` solo llega en el cambio obligatorio */
export const validateNewPassword = (
  newPassword: string,
  repeatedPassword: string,
  previousPassword?: string | null,
) => {
  const isSixDigits = (value: string) => new RegExp(`^\\d{${PASSWORD_LENGTH}}$`).test(value);
  const newComplete = isSixDigits(newPassword);

  const reused = newComplete && !!previousPassword && newPassword === previousPassword;
  const strengthError = newComplete ? getPasswordStrengthError(newPassword) : null;
  const mismatch = newComplete && isSixDigits(repeatedPassword) && newPassword !== repeatedPassword;
  const isValid = newComplete && newPassword === repeatedPassword && !strengthError && !reused;

  return {
    isValid,
    // Reutilizar la clave anterior va primero
    errorMessage: reused
      ? PASSWORD_REUSED_ERROR
      : strengthError ?? (mismatch ? PASSWORD_MISMATCH_ERROR : null),
  };
};
